import { useContext } from 'react';
import { graphql, useStaticQuery } from 'gatsby';
import {
	enrollmentContext,
	InputQuestion,
} from '../components/hoc/context/enrollment/enrollment-context';

export type QuestionChoice = {
	value: string | boolean | number;
	label: string;
	description?: ChoiceDescription;
};

export type ChoiceDescription = {
	header?: string;
	body?: string;
};

export type QuestionParent = {
	id: string;
	value: boolean | string;
	operator?: string;
};
export type QuestionConditions = {
	parents: QuestionParent[];
	discardedServices?: string[];
};

export interface Question {
	id: string;
	type: string;
	header?: string;
	label?: string;
	title?: string;
	titleClassName?: string;
	description?: string;
	descriptionClassName?: string;
	choices?: QuestionChoice[];
	help?: string[];
	buttonLabel?: string;
	mlrVersion?: string;
	performer?: string;
	conditions?: QuestionConditions;
}

export interface QuestionGroup {
	id: string;
	// TODO: refactor to questions
	question: Question[];
	filterQuestions(): Question[];
}

export interface Configuration {
	group: QuestionGroup[];
	getGroup(id: string): QuestionGroup | undefined;
}

export default function useQuestions(): Configuration {
	const { performer, input } = useContext(enrollmentContext);
	const { config } = useStaticQuery<{ config: Configuration }>(graphql`
		query {
			config: questionsConfiguration {
				group {
					id
					question {
						choices {
							label
							value
							description {
								body
								header
							}
						}
						description
						descriptionClassName
						id
						performer
						title
						titleClassName
						type
						conditions {
							parents {
								id
								operator
								value
							}
						}
					}
				}
			}
		}
	`);

	config.getGroup = (id) => {
		return config.group.find((group: QuestionGroup) => {
			return group.id === id;
		});
	};

	const checkAllServicesDiscarded = (
		discarded: string[] | undefined
	): boolean => {
		return discarded
			? !discarded?.every(
					(service) => !input.discardedServices.includes(service)
			  )
			: false;
	};

	const getPreviousAnswerValue = (previousAnswer: InputQuestion) => {
		let previousAnswerValue = previousAnswer.answer;
		if (
			previousAnswer?.type === 'boolean' &&
			typeof previousAnswerValue === 'boolean'
		) {
			previousAnswerValue = previousAnswerValue.toString();
		}
		return previousAnswerValue;
	};

	const checkParentCondition = (condition: QuestionParent) => {
		const previousAnswer = input.questions.find(
			(answer) => answer.id === condition.id
		);
		if (previousAnswer) {
			// cast true and false to 'true' and 'false'
			const previousAnswerValue = getPreviousAnswerValue(previousAnswer);
			return condition.operator === 'not'
				? condition.value !== previousAnswerValue
				: condition.value === previousAnswerValue;
		}
		return false;
	};

	config.group.forEach((group: QuestionGroup) => {
		group.filterQuestions = () => {
			return group.question.filter((question) => {
				if (question.performer && question.performer !== performer) {
					return false;
				}
				// Check conditions
				if (!question.conditions) {
					return true;
				}
				if (checkAllServicesDiscarded(question.conditions.discardedServices)) {
					return false;
				}
				if (
					!question.conditions.parents ||
					!question.conditions.parents.length ||
					!input.questions.length
				) {
					return true;
				}
				return question.conditions.parents.every(checkParentCondition);
			});
		};
	});

	return config;
}
